import { useAuthStore } from '../stores/authStore'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://agwnmyebk4.execute-api.us-east-1.amazonaws.com/dev'

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const { accessToken, refreshAccessToken, logout } = useAuthStore.getState()

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    let response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    })

    // Handle token expiration
    if (response.status === 401) {
      try {
        await refreshAccessToken()
        const newToken = useAuthStore.getState().accessToken
        
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`
          response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers,
          })
        }
      } catch (error) {
        logout()
        throw new Error('Session expired')
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }))
      throw new Error(error.error || error.message || 'Request failed')
    }

    return response.json()
  }

  // Materials
  async uploadMaterial(file: File) {
    // Step 1: request presigned S3 URL (file never goes through Lambda)
    const { uploadUrl, materialId } = await this.request('/materials/request-upload', {
      method: 'POST',
      body: JSON.stringify({ filename: file.name, content_type: file.type, size: file.size }),
    }) as { uploadUrl: string; materialId: string }

    // Step 2: PUT directly to S3
    const s3Response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    })
    if (!s3Response.ok) throw new Error('Error al subir el archivo a S3.')

    return { materialId }
  }

  async processMaterial(materialId: string) {
    return this.request(`/materials/${materialId}/process`, {
      method: 'POST',
    })
  }

  async getMaterials() {
    return this.request('/materials')
  }

  async getMaterial(materialId: string) {
    return this.request(`/materials/${materialId}`)
  }

  async deleteMaterial(materialId: string) {
    return this.request(`/materials/${materialId}`, {
      method: 'DELETE',
    })
  }

  // Study
  async generateSummary(materialId: string, studentLevel: string = 'secondary', summaryType: string = 'comprehensive') {
    return this.request('/study/generate', {
      method: 'POST',
      body: JSON.stringify({ 
        material_id: materialId,
        content_type: 'summary',
        student_level: studentLevel,
        summary_type: summaryType
      }),
    })
  }

  async generateQuiz(materialId: string, studentLevel: string = 'secondary') {
    return this.request('/study/generate', {
      method: 'POST',
      body: JSON.stringify({ 
        material_id: materialId,
        content_type: 'quiz',
        student_level: studentLevel
      }),
    })
  }

  async generateMindmap(materialId: string, studentLevel: string = 'secondary') {
    return this.request('/study/generate', {
      method: 'POST',
      body: JSON.stringify({ 
        material_id: materialId,
        content_type: 'mindmap',
        student_level: studentLevel
      }),
    })
  }

  async retryGeneration(materialId: string, contentType: 'summary' | 'quiz' | 'mindmap') {
    return this.request('/study/retry-generation', {
      method: 'POST',
      body: JSON.stringify({
        material_id: materialId,
        content_type: contentType
      }),
    })
  }

  async regenerateSummary(materialId: string, detailLevel: 'standard' | 'comprehensive' = 'standard') {
    return this.request('/study/regenerate-summary', {
      method: 'POST',
      body: JSON.stringify({ material_id: materialId, detail_level: detailLevel }),
    })
  }

  async exportSummaryPdf(materialId: string, detailLevel: 'standard' | 'comprehensive' = 'standard'): Promise<Blob> {
    const { accessToken, refreshAccessToken, logout } = useAuthStore.getState()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    let response = await fetch(`${API_BASE_URL}/study/${materialId}/export-pdf`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ detail_level: detailLevel }),
    })

    if (response.status === 401) {
      try {
        await refreshAccessToken()
        const newToken = useAuthStore.getState().accessToken
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`
          response = await fetch(`${API_BASE_URL}/study/${materialId}/export-pdf`, {
            method: 'POST', headers, body: JSON.stringify({ detail_level: detailLevel }),
          })
        }
      } catch { logout(); throw new Error('Session expired') }
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Error generando PDF' }))
      throw new Error(err.detail || err.error || 'Error generando PDF')
    }

    return response.blob()
  }

  async regenerateQuiz(materialId: string, questionCount: number = 15, difficulty: 'easy' | 'medium' | 'hard' = 'medium') {
    return this.request('/study/regenerate-quiz', {
      method: 'POST',
      body: JSON.stringify({
        material_id: materialId,
        question_count: questionCount,
        difficulty: difficulty
      }),
    })
  }

  async getQuizVersions(materialId: string): Promise<{ versions: import('../types').QuizVersionMeta[] }> {
    return this.request(`/study/${materialId}/quiz-versions`)
  }

  async getQuizVersion(materialId: string, versionId: string): Promise<{
    version_id: string
    created_at: string
    difficulty: string
    question_count: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    content: any
  }> {
    return this.request(`/study/${materialId}/quiz-versions/${versionId}`)
  }

  // Results
  async submitQuizResults(data: {
    session_id: string
    material_id: string
    answers: Array<{
      question_id: string
      user_answer: string
      correct: boolean
      time_taken?: number | null
    }>
    time_spent: number
    difficulty?: string
    quiz_version_id?: string
  }) {
    return this.request('/results/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getUserResults() {
    return this.request('/results/')
  }

  async getMaterialResults(materialId: string) {
    return this.request(`/results/material/${materialId}`)
  }

  async getResultStats(materialId?: string) {
    const qs = materialId ? `?material_id=${materialId}` : ''
    return this.request(`/results/stats/summary${qs}`)
  }

  // Billing
  async createCheckoutSession(priceId: string): Promise<{ url: string }> {
    return this.request('/billing/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ price_id: priceId }),
    })
  }

  async getBillingPortal(): Promise<{ url: string }> {
    return this.request('/billing/portal')
  }

  // Family
  async generateLinkCode(): Promise<{ code: string; expires_in_hours: number; deep_link: string }> {
    return this.request('/family/generate-code', { method: 'POST' })
  }

  async useLinkCode(code: string): Promise<{ linked: boolean; parent_id: string }> {
    return this.request('/family/use-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })
  }

  async getLinkedStudents(): Promise<{ students: Array<{
    id: string; name: string; email: string;
    avg_score: number; quiz_count: number; last_activity: string | null
  }> }> {
    return this.request('/family/students')
  }

  async getStudentResults(studentId: string): Promise<{ results: unknown[]; student_id: string }> {
    return this.request(`/family/students/${studentId}/results`)
  }

  async getChildren(): Promise<{ children: Array<{
    userId: string; name: string; email: string; grade: string;
    status: 'improving' | 'needs_support' | 'stable' | 'neutral';
    materialsThisWeek: number; lastQuizScore: number;
    overallProgress: number; lastActiveAt: string | null
  }> }> {
    return this.request('/family/children')
  }

  async getChildActivity(childId: string): Promise<{ activity: Array<{
    type: 'quiz' | 'material'; description: string;
    materialId: string | null; timestamp: string | null
  }>; child_id: string }> {
    return this.request(`/family/children/${childId}/activity`)
  }

  async unlinkChild(childId: string): Promise<{ unlinked: boolean; child_id: string }> {
    return this.request(`/family/children/${childId}`, { method: 'DELETE' })
  }

  // ==================== COURSES ====================

  async listCourses() {
    return this.request<{ courses: import('../types').Course[]; total: number }>('/courses')
  }

  async getCourse(courseId: string) {
    return this.request<import('../types').CourseDetail>(`/courses/${courseId}`)
  }

  async createCourse(payload: {
    name: string
    color?: string
    examDate?: string
    description?: string
    materialIds?: string[]
  }) {
    return this.request<import('../types').Course>('/courses', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async updateCourse(
    courseId: string,
    payload: Partial<{ name: string; color: string; examDate: string; description: string }>,
  ) {
    return this.request<import('../types').Course>(`/courses/${courseId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  async deleteCourse(courseId: string) {
    return this.request<{ deleted: string }>(`/courses/${courseId}`, { method: 'DELETE' })
  }

  async addMaterialToCourse(courseId: string, materialId: string) {
    return this.request<import('../types').Course>(`/courses/${courseId}/materials`, {
      method: 'POST',
      body: JSON.stringify({ materialId }),
    })
  }

  async removeMaterialFromCourse(courseId: string, materialId: string) {
    return this.request<import('../types').Course>(
      `/courses/${courseId}/materials/${materialId}`,
      { method: 'DELETE' },
    )
  }

  async generateCourseQuiz(
    courseId: string,
    payload: { question_count?: number; difficulty?: 'easy' | 'medium' | 'hard' } = {},
  ) {
    return this.request<{
      course_id: string
      status: string
      question_count: number
      difficulty: string
    }>(`/courses/${courseId}/quiz`, {
      method: 'POST',
      body: JSON.stringify({
        question_count: payload.question_count ?? 15,
        difficulty: payload.difficulty ?? 'medium',
      }),
    })
  }

  async listCourseQuizVersions(courseId: string) {
    return this.request<{
      versions: Array<{
        version_id: string
        created_at: string
        difficulty: 'easy' | 'medium' | 'hard'
        question_count: number
      }>
    }>(`/courses/${courseId}/quiz-versions`)
  }

  async getCourseQuizVersion(courseId: string, versionId: string) {
    return this.request<import('../types').CourseQuizVersion>(
      `/courses/${courseId}/quiz-versions/${versionId}`,
    )
  }

  // ==================== FLASHCARDS (Sprint 1-B) ====================

  async generateFlashcards(materialId: string, count: number = 15) {
    return this.request<{ material_id: string; status: string; count: number }>(
      '/flashcards/generate',
      {
        method: 'POST',
        body: JSON.stringify({ material_id: materialId, count }),
      },
    )
  }

  async getDueFlashcards(limit: number = 50) {
    return this.request<{ items: import('../types').Flashcard[]; count: number }>(
      `/flashcards/due?limit=${limit}`,
    )
  }

  async getFlashcardsByMaterial(materialId: string) {
    return this.request<{ items: import('../types').Flashcard[]; count: number }>(
      `/flashcards/by-material/${materialId}`,
    )
  }

  async reviewFlashcard(cardId: string, quality: number) {
    return this.request<{ card: import('../types').Flashcard }>(
      `/flashcards/${cardId}/review`,
      {
        method: 'POST',
        body: JSON.stringify({ quality }),
      },
    )
  }

  async editFlashcard(cardId: string, payload: { front?: string; back?: string }) {
    return this.request<{ card: import('../types').Flashcard }>(`/flashcards/${cardId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }

  async createFlashcard(payload: { material_id: string; front: string; back: string }) {
    return this.request<{ card: import('../types').Flashcard }>('/flashcards', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async deleteFlashcard(cardId: string) {
    return this.request<{ deleted: boolean }>(`/flashcards/${cardId}`, {
      method: 'DELETE',
    })
  }

  // ==================== CHAT (Sprint 1-C) ====================

  async sendChatMessage(materialId: string, content: string) {
    return this.request<{
      userMessageId: string
      assistantMessageId: string
      status: 'streaming'
    }>(`/chat/${materialId}/message`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    })
  }

  async getChatHistory(materialId: string) {
    return this.request<{
      messages: import('../types').ChatMessage[]
      count: number
    }>(`/chat/${materialId}/history`)
  }

  async deleteChatHistory(materialId: string) {
    return this.request<{ deleted: number }>(
      `/chat/${materialId}/history`,
      { method: 'DELETE' },
    )
  }
}

export const api = new ApiClient()
